import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFarsi = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng   = s => s.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum  = n => n > 0 ? Number(n).toLocaleString('fa-IR') : '•';

const EXEC_TYPES = ['امانی','پیمانی','امانی-پیمانی'];
const EMPTY = {
  code:'',title:'',is_project:true,is_active:true,
  exec_type:0,exception_pct:0,start_date:'',end_date:'',
  main_project_id:0,dept_id:0,city_id:0,
};
const API = (path,opts={}) => fetch(`/api/accounting${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},
  ...opts,
}).then(jsonOf, netErr);

const FInput = ({label,name,value,onChange,type='text',half,placeholder}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:half?'0 0 48%':'1'}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder||''}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',width:'100%',boxSizing:'border-box'}}/>
  </div>
);
const FSelect = ({label,name,value,onChange,opts}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:1}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',background:'#fff'}}>
      {opts.map((o,i)=><option key={i} value={i}>{o}</option>)}
    </select>
  </div>
);
const FCheck = ({label,name,checked,onChange}) => (
  <label style={{display:'flex',alignItems:'center',fontSize:'12px',color:'#444',cursor:'pointer',padding:'4px 8px',background:'#fff',borderRadius:'6px',border:'1px solid #e0e0e0'}}>
    <input type="checkbox" name={name} checked={checked} onChange={onChange} style={{marginLeft:'6px'}}/>
    {label}
  </label>
);

// initialType: با چه فیلتری باز شود — all=هر دو ، project=پروژه‌ها ، activity=فعالیت‌ها
export default function AccountingProject({user,onBack,initialType='all'}) {
  // دکمه‌های فرم FRMPROJECT؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMPROJECT', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const curYear = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  // فیلتر نمایش: همه / فقط پروژه / فقط فعالیت
  const [ptype,    setPtype]    = useState(initialType);   // all | project | activity
  const isActivityMode = initialType === 'activity';       // وقتی از منوی فعالیت‌ها باز شده

  const [year,     setYear]     = useState(curYear);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [search,   setSearch]   = useState('');
  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');
  const [dateInfo, setDateInfo] = useState({from:'',to:''});

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),3000); };

  // ── Pagination 200 تایی مثل سند حسابداری ──
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [hasMore,  setHasMore]  = useState(false);
  const [loadMore, setLoadMore] = useState(false);

  // ── Resize ستون‌ها ──
  const initW = [80,180,55,45,130,130,90,60,55,50,80,80,110,100,100];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(()=>{ colWRef.current = colW; },[colW]);
  const startResize = useCallback((idx,e)=>{
    e.preventDefault(); e.stopPropagation();
    const sx=e.clientX, sw=colWRef.current[idx];
    const onMove=ev=>{
      const newW=Math.max(40,sw+(sx-ev.clientX));
      setColW(w=>{const n=[...w];n[idx]=newW;return n;});
    };
    const onUp=()=>{document.removeEventListener('mousemove',onMove);document.removeEventListener('mouseup',onUp);};
    document.addEventListener('mousemove',onMove);
    document.addEventListener('mouseup',onUp);
  },[]);

  const load = useCallback(async (y,df,dt) => {
    const yr = y  || year;
    const f  = df !== undefined ? df : dateFrom;
    const t  = dt !== undefined ? dt : dateTo;
    setLoading(true); setError(''); setPage(1);
    let url = `/projects/?year=${yr}&search=${encodeURIComponent(search)}&type=${ptype}&page=1`;
    if (f) url += `&from=${f}`;
    if (t) url += `&to=${t}`;
    const j = await API(url);
    setLoading(false);
    if (j.ok) {
      setData(j.data);
      setTotal(j.total || j.data.length);
      setHasMore(j.has_more || false);
      setDateInfo({from:j.date_from,to:j.date_to});
    } else setError(j.msg);
  }, [search, year, dateFrom, dateTo, ptype]);

  const loadNextPage = async () => {
    const next = page + 1;
    setLoadMore(true);
    const yr = year;
    const f  = dateFrom;
    const t  = dateTo;
    let url = `/projects/?year=${yr}&search=${encodeURIComponent(search)}&type=${ptype}&page=${next}`;
    if (f) url += `&from=${f}`;
    if (t) url += `&to=${t}`;
    const j = await API(url);
    setLoadMore(false);
    if (j.ok) {
      setData(d=>[...d,...j.data]);
      setHasMore(j.has_more || false);
      setPage(next);
    }
  };

  useEffect(()=>{ load(); },[ptype]);   // بار اول + با هر تغییر فیلتر پروژه/فعالیت

  const handleYearChange = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g,'')).slice(0,4);
    setYear(v);
    if(v.length===4) load(v, dateFrom, dateTo);
  };

  const filtered = data.filter(r =>
    !search || r.title.includes(search) || r.code.includes(search)
  );

  const F  = (k,v) => setForm(f=>({...f,[k]:v}));
  // رکورد جدید: اگر فیلتر روی فعالیت است، پیش‌فرض «فعالیت» (IsProject=0) وگرنه «پروژه»
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm({...EMPTY, is_project: ptype!=='activity'}); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return; if(!selected) return; setForm({...selected}); setModal('edit'); };
  const openDel  = () => { if (!vbf.can('BTNDELETE')) return; if(!selected) return; setModal('delete'); };

  const save = async () => {
    if(!form.title.trim()){setError('عنوان الزامی است');return;}
    if(!form.code.trim()) {setError('کد الزامی است');return;}
    setSaving(true); setError('');
    const j = modal==='new'
      ? await API('/projects/create/',{method:'POST',body:JSON.stringify(form)})
      : await API(`/projects/${selected.id}/update/`,{method:'PUT',body:JSON.stringify(form)});
    setSaving(false);
    if(j.ok){setModal(null);setSelected(null);showToast(j.msg);load();}
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/projects/${selected.id}/delete/`,{method:'DELETE'});
    setSaving(false);
    if(j.ok){setModal(null);setSelected(null);showToast('حذف شد');load();}
    else{setError(j.msg);setModal(j.used?'delete_blocked':null);}
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}>
            <img src={require('../../assets/images/arjan.png')} alt="ارجان"
              style={{width:'34px',height:'34px',borderRadius:'7px'}}/>
          </div>
          <div>
            <div style={s.hTitle}>{isActivityMode ? 'فعالیت‌ها' : 'پروژه‌ها'}</div>
            <div style={s.hSub}>تنظیم حساب — سامانه ارجان</div>
          </div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>

      <TabBar />

      <main style={s.main}>
        {/* نوار ابزار */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected}
              style={{...s.btnEdit,opacity:selected?1:0.4,cursor:selected?'pointer':'default'}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected}
              style={{...s.btnDel,opacity:selected?1:0.4,cursor:selected?'pointer':'default'}}>✕ حذف</button>}
          </div>

          {/* فیلتر نوع: همه / پروژه / فعالیت */}
          <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
            <label style={{fontSize:'10px',fontWeight:'600',color:'#555'}}>نوع</label>
            <div style={{display:'flex',gap:'4px'}}>
              {[['all','همه'],['project','پروژه'],['activity','فعالیت']].map(([v,lbl])=>(
                <button key={v} onClick={()=>{setSelected(null);setPtype(v);}}
                  style={{padding:'6px 12px',borderRadius:'7px',border:'none',cursor:'pointer',
                    fontFamily:'inherit',fontSize:'12px',fontWeight:'700',
                    background:ptype===v?'#1a2f50':'#e8f0fe',
                    color:ptype===v?'#C8A84B':'#1a2f50'}}>{lbl}</button>
              ))}
            </div>
          </div>

          {/* سال مالی */}
          <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
            <label style={{fontSize:'10px',fontWeight:'600',color:'#555'}}>سال مالی</label>
            <input value={toFarsi(year)} onChange={handleYearChange}
              style={{width:'80px',padding:'6px 8px',border:'2px solid #C8A84B',borderRadius:'7px',fontSize:'14px',fontWeight:'700',textAlign:'center',fontFamily:'monospace',outline:'none',color:'#1a2f50'}}
              maxLength={4} placeholder="۱۴۰۵"/>
          </div>

          {/* از تاریخ */}
          <div style={{display:'flex',flexDirection:'column',gap:'3px',width:'140px'}}>
            <label style={{fontSize:'10px',fontWeight:'600',color:'#555'}}>از تاریخ</label>
            <JalaliDatePicker value={dateFrom}
              onChange={v=>{setDateFrom(v); load(year,v,dateTo);}}
              placeholder="از تاریخ"/>
          </div>

          {/* تا تاریخ */}
          <div style={{display:'flex',flexDirection:'column',gap:'3px',width:'140px'}}>
            <label style={{fontSize:'10px',fontWeight:'600',color:'#555'}}>تا تاریخ</label>
            <JalaliDatePicker value={dateTo}
              onChange={v=>{setDateTo(v); load(year,dateFrom,v);}}
              placeholder="تا تاریخ"/>
          </div>

          <input value={search} onChange={e=>setSearch(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&load()}
            style={{...s.searchInput,flex:1,minWidth:'150px'}} placeholder="جستجو..."/>

          {loading && <span>⏳</span>}
          {!loading && filtered.length>0 &&
            <div style={s.countBadge}>{toFarsi(filtered.length)} از {toFarsi(total||filtered.length)} مورد</div>}
        </div>

        {error && !modal && <div style={s.errBox}>⚠️ {error}</div>}

        {/* جدول */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead style={{position:'sticky',top:0,zIndex:10}}>
              <tr>
                {[
                  {l:'شماره',       bg:'#1a2f50',tc:'#fff'},
                  {l:'عنوان',       bg:'#1a2f50',tc:'#fff'},
                  {l:'درصد استثناء',bg:'#1a2f50',tc:'#fff'},
                  {l:'فعال',        bg:'#1a2f50',tc:'#fff'},
                  {l:'منبع اعتبار', bg:'#1a2f50',tc:'#fff'},
                  {l:'طرح/برنامه',  bg:'#1a2f50',tc:'#fff'},
                  {l:'واحد اجرایی', bg:'#1a2f50',tc:'#fff'},
                  {l:'شهر',         bg:'#1a2f50',tc:'#fff'},
                  {l:'نحوه اجرا',   bg:'#1a2f50',tc:'#fff'},
                  {l:'نوع',         bg:'#1a2f50',tc:'#fff'},
                  {l:'شروع',        bg:'#1a2f50',tc:'#fff'},
                  {l:'خاتمه',       bg:'#1a2f50',tc:'#fff'},
                  {l:'اعتبار مصوب', bg:'#1b5e20',tc:'#fff'},
                  {l:'تخصیص',       bg:'#0d2137',tc:'#C8A84B'},
                  {l:'پرداختی',     bg:'#b71c1c',tc:'#fff'},
                ].map((h,i)=>(
                  <th key={i} style={{...s.th,width:colW[i],minWidth:40,background:h.bg,color:h.tc,position:'relative',userSelect:'none'}}>
                    {h.l}
                    <span onMouseDown={e=>startResize(i,e)}
                      style={{position:'absolute',left:0,top:0,bottom:0,width:'4px',cursor:'col-resize',background:'transparent'}}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r,i)=>{
                const sel = selected?.id===r.id;
                const bg  = sel?'#1a2f50':i%2===0?'#fff':'#fdf6e3';
                return (
                  <tr key={r.id} style={{background:bg,cursor:'pointer',transition:'background 0.1s'}}
                    onClick={()=>setSelected(sel?null:r)}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#e8f0fe';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={{...s.td,fontWeight:'700',color:sel?'#C8A84B':'#1a237e',fontFamily:'monospace'}}>{r.code}</td>
                    <td style={{...s.td,textAlign:'right',fontWeight:'600',fontSize:'12px',color:sel?'#fff':'#1a1a2e',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'180px'}} title={r.title}>{r.title}</td>
                    <td style={{...s.td,fontSize:'11px',color:sel?'rgba(255,255,255,0.8)':'#555'}}>{r.exception_pct>0?toFarsi(r.exception_pct)+'٪':'•'}</td>
                    <td style={s.td}>{r.is_active?<span style={{color:sel?'#81c784':'#1b5e20',fontWeight:'700',fontSize:'13px'}}>✓</span>:''}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'130px'}} title={r.credit_source}>{r.credit_source||'•'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'130px'}} title={r.program}>{r.program||'•'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.dept}>{r.dept||'•'}</td>
                    <td style={{...s.td,fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555'}}>{r.city||'•'}</td>
                    <td style={{...s.td,fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555'}}>{r.exec_name}</td>
                    <td style={s.td}>
                      <span style={{padding:'1px 5px',borderRadius:'6px',fontSize:'10px',fontWeight:'700',
                        background:sel?'rgba(255,255,255,0.15)':r.is_project?'#e8eaf6':'#e8f5e9',
                        color:sel?'#fff':r.is_project?'#1a237e':'#1b5e20'}}>
                        {r.type_name}
                      </span>
                    </td>
                    <td style={{...s.td,fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555'}}>{r.start_date||'•'}</td>
                    <td style={{...s.td,fontSize:'10px',color:sel?'rgba(255,255,255,0.8)':'#555'}}>{r.end_date||'•'}</td>
                    <td style={{...s.tdNum,color:sel?'#81c784':r.credit_approved>0?'#1b5e20':'#ccc'}}>{fmtNum(r.credit_approved)}</td>
                    <td style={{...s.tdNum,color:sel?'#C8A84B':r.credit_allocate>0?'#1a237e':'#ccc'}}>{fmtNum(r.credit_allocate)}</td>
                    <td style={{...s.tdNum,fontWeight:'700',color:sel?'#ffb74d':r.credit_paid>0?'#b71c1c':'#ccc'}}>{fmtNum(r.credit_paid)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading&&filtered.length===0&&<div style={s.empty}>📭 داده‌ای یافت نشد</div>}
        </div>

        {/* دکمه ۲۰۰تایی — همیشه ته صفحه، با جدول اسکرول نمی‌شود */}
        {hasMore && !search && (
          <div style={s.pagerBar}>
            <button onClick={loadNextPage} disabled={loadMore} style={{
              padding:'9px 36px',
              background:loadMore?'#ccc':'linear-gradient(135deg,#1a2f50,#1a237e)',
              color:'#fff',border:'none',borderRadius:'8px',
              cursor:loadMore?'default':'pointer',
              fontFamily:'inherit',fontSize:'13px',fontWeight:'700',
            }}>
              {loadMore?'⏳ در حال بارگذاری...'
                :`▼ ۲۰۰ مورد بعدی (${toFarsi(String((total||0)-(data||[]).length))} باقی‌مانده)`}
            </button>
          </div>
        )}
      </main>

      {/* پاپ‌آپ جدید/ویرایش */}
      {(modal==='new'||modal==='edit')&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{modal==='new'?'＋ پروژه/فعالیت جدید':`✎ ویرایش: ${selected?.title}`}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>⚠️ {error}</div>}
              <div style={s.row}>
                <FInput label="کد" name="code" value={form.code} onChange={e=>F('code',e.target.value)} half/>
                <FInput label="درصد استثناء" name="exception_pct" value={form.exception_pct} onChange={e=>F('exception_pct',Number(e.target.value))} type="number" half/>
              </div>
              <div style={s.row}>
                <FInput label="عنوان" name="title" value={form.title} onChange={e=>F('title',e.target.value)}/>
              </div>
              <div style={s.row}>
                <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:'0 0 48%'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>تاریخ شروع</label>
                  <JalaliDatePicker value={form.start_date} onChange={v=>F('start_date',v)} placeholder="انتخاب تاریخ شروع"/>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:'0 0 48%'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>تاریخ خاتمه</label>
                  <JalaliDatePicker value={form.end_date} onChange={v=>F('end_date',v)} placeholder="انتخاب تاریخ خاتمه"/>
                </div>
              </div>
              <div style={s.row}>
                <FSelect label="نحوه اجرا" name="exec_type" value={form.exec_type} onChange={e=>F('exec_type',Number(e.target.value))} opts={EXEC_TYPES}/>
              </div>
              <div style={s.divider}/>
              <div style={s.chkGroup}>
                <div style={s.chkTitle}>⚙️ تنظیمات</div>
                <div style={s.chkRow}>
                  <FCheck label="پروژه" name="is_project" checked={form.is_project} onChange={e=>F('is_project',e.target.checked)}/>
                  <FCheck label="فعال" name="is_active" checked={form.is_active} onChange={e=>F('is_active',e.target.checked)}/>
                </div>
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{setModal(null);setError('');}} style={s.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={s.btnSave}>{saving?'⏳ ...':'💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {/* حذف */}
      {modal==='delete'&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'440px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={{...s.mBody,textAlign:'center',padding:'28px'}}>
              <div style={{fontSize:'36px',marginBottom:'12px'}}>🗑️</div>
              <div style={{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',fontWeight:'700',color:'#b71c1c'}}>
                [{selected?.code}] {selected?.title}
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{...s.btnSave,background:'#b71c1c'}}>{saving?'⏳...':'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal==='delete_blocked'&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'400px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#e65100,#f57c00)'}}>
              <div style={s.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={{...s.mBody,textAlign:'center',padding:'28px'}}>
              <div style={{fontSize:'36px',marginBottom:'12px'}}>⚠️</div>
              <div style={{fontSize:'14px',color:'#1a2f50',fontWeight:'700',lineHeight:'1.8'}}>
                این رکورد در جام ارجان مورد استفاده قرار گرفته<br/>و قابل حذف نیست
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{setModal(null);setError('');}} style={s.btnSave}>باشه</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:      {height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:    {background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'12px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:     {display:'flex',alignItems:'center',gap:'12px'},
  backBtn:   {padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:     {width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:    {fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:      {fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:  {fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:      {padding:'12px 24px',flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0},
  toolbar:   {display:'flex',alignItems:'flex-end',gap:'10px',background:'#fff',padding:'14px 18px',borderRadius:'12px',boxShadow:'0 2px 8px rgba(0,0,0,0.07)',marginBottom:'12px',flexWrap:'wrap',flexShrink:0},
  pagerBar:  {textAlign:'center',padding:'10px 0 2px',flexShrink:0},
  toolBtns:  {display:'flex',gap:'8px',alignSelf:'flex-end'},
  btnNew:    {padding:'9px 18px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:   {padding:'9px 18px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnDel:    {padding:'9px 18px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  searchInput:{padding:'8px 12px',border:'2px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:'inherit',outline:'none'},
  countBadge:{padding:'5px 12px',background:'#1a2f50',color:'#C8A84B',borderRadius:'20px',fontSize:'12px',fontWeight:'700',alignSelf:'flex-end'},
  errBox:    {background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'12px',fontSize:'13px'},
  tableWrap: { flex:1,minHeight:0,overflowY:'auto',overflowX:'auto',borderRadius:'12px',boxShadow:'0 2px 12px rgba(0,0,0,0.1)',background:'#fff'},
  table:     {width:'100%',borderCollapse:'collapse'},
  th:        {padding:'9px 8px',background:'#1a2f50',color:'#fff',fontWeight:'700',textAlign:'center',whiteSpace:'nowrap',borderLeft:'1px solid rgba(255,255,255,0.1)',fontFamily:'inherit',fontSize:'12px'},
  td:        {padding:'6px 7px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  tdNum:     {padding:'6px 7px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",fontWeight:'600',whiteSpace:'nowrap'},
  empty:     {textAlign:'center',padding:'40px',color:'#999'},
  overlay:   {position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:     {background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'600px',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:   {display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:    {fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:  {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center'},
  mBody:     {overflowY:'auto',flex:1,padding:'20px'},
  mFooter:   {display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 20px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:       {display:'flex',gap:'14px',marginBottom:'12px',flexWrap:'wrap'},
  divider:   {height:'1px',background:'#f0f2f7',margin:'12px 0'},
  chkGroup:  {background:'#f8f9fa',borderRadius:'10px',padding:'14px 16px',border:'1px solid #e8eaf6'},
  chkTitle:  {fontSize:'12px',fontWeight:'700',color:'#1a237e',marginBottom:'10px'},
  chkRow:    {display:'flex',flexWrap:'wrap',gap:'10px'},
  btnCancel: {padding:'8px 20px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:   {padding:'8px 24px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:     {position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};